<script>
    import Logo from "@propublica/pp-v5-shared/src/icons/propublica-logo.svg";
    import NewsletterSignup from "../NewsletterSignup";
    import NewsletterModal from "../NewsletterModal";

    /**
     * Hides the newsletter signup box in the footer.
     * @type {boolean}
     */
    export let hideNewsletterSignup = false;

    /**
     * Disables the newsletter modal.
     *
     * This is false by default for backward compatibility, but we recommend
     * that you change it to true and use `NewsletterModal` separately from
     * `SiteFooter`. In a future breaking change, we (eventually) plan to remove
     * the `NewsletterModal` nested in `SiteFooter`.
     * @type {boolean}
     */
    export let disableNewsletterModal = false;

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    let activeLinks = "";

    function setActiveLinks(list) {
        if (activeLinks === list) {
            activeLinks = "";
        } else {
            activeLinks = list;
        }
    }
</script>

<footer class="site-footer" id="site-footer" data-pp-location="footer">
    {#if hideNewsletterSignup !== true}
        <div class="site-footer__section-newsletter">
            <div class="site-footer__newsletter-wrap">
                <strong class="site-footer__newsletter-heading"
                    >Stay informed with the Daily&nbsp;Digest.</strong
                >
                <div class="newsletter-signup__input-wrap">
                    <NewsletterSignup
                        newsletterList="The Daily Digest"
                        placeholderText="Enter your email"
                        inputID="site-footer-signup"
                        placement="site-footer"
                    />
                </div>
            </div>
            <!-- end .site-footer__newsletter-wrap -->
        </div>
        <!-- end .site-footer__section-newsletter -->
    {/if}

    <div class="site-footer__wrap-outer">
        <div class="site-footer__wrap-inner">
            <!-- Logo -->

            <div class="site-footer__section-logo">
                <a class="site-footer__wordmark" href="http://propublica.org/">
                    <Logo />
                </a>
                <strong class="site-footer__tagline"
                    >Investigative Journalism in the Public Interest</strong
                >
                <p class="site-footer__copyright">
                    © Copyright {currentYear} Pro Publica Inc.
                </p>
            </div>
            <!-- end .site-footer__section-logo -->

            <!-- Links -->

            <nav class="site-footer__section-links">
                <!-- Sites -->

                <div
                    class="site-footer__links-sites"
                    on:click={() => setActiveLinks("sites")}
                >
                    <strong class="site-footer__links-heading">Sites</strong>
                    <ul
                        class="site-footer__links-list"
                        class:hide-below-sm={activeLinks !== "sites"}
                    >
                        <li class="site-footer__link">
                            <a href="http://propublica.org/">ProPublica</a>
                        </li>
                        <li class="site-footer__link">
                            <a
                                href="http://propublica.org/local-reporting-network/"
                                >Local Reporting Network</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/texas/"
                                >Texas Tribune Partnership</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/datastore/"
                                >The Data Store</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/electionland/"
                                >Electionland</a
                            >
                        </li>
                    </ul>
                </div>
                <!-- end .site-footer__links-sites -->

                <!-- Sections -->

                <div
                    class="site-footer__links-sections"
                    on:click={() => setActiveLinks("sections")}
                >
                    <strong class="site-footer__links-heading">Sections</strong>
                    <ul
                        class="site-footer__links-list"
                        class:hide-below-sm={activeLinks !== "sections"}
                    >
                        <li class="site-footer__link">
                            <a href="http://propublica.org/topics/">Topics</a>
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/series/">Series</a>
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/video/">Videos</a>
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/newsapps/"
                                >News Apps</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/getinvolved/"
                                >Get Involved</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/nerds/"
                                >The Nerd Blog</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/atpropublica/"
                                >@ProPublica</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/events/">Events</a>
                        </li>
                    </ul>
                </div>
                <!-- end .site-footer__links-sections -->

                <!-- Info -->

                <div
                    class="site-footer__links-info"
                    on:click={() => setActiveLinks("info")}
                >
                    <strong class="site-footer__links-heading">Info</strong>
                    <ul
                        class="site-footer__links-list"
                        class:hide-below-sm={activeLinks !== "info"}
                    >
                        <li class="site-footer__link">
                            <a href="http://propublica.org/about/">About</a>
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/leadership/"
                                >Board and Advisors</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/staff/"
                                >Officers and Staff</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/diversity/"
                                >Diversity</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/jobs/">Jobs</a> and
                            <a href="http://propublica.org/fellowships/"
                                >Fellowships</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/media-center/"
                                >Media Center</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/reports/">Reports</a>
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/impact/">Impact</a>
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/awards/">Awards</a>
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/corrections/"
                                >Corrections</a
                            >
                        </li>
                    </ul>
                </div>
                <!-- end .site-footer__links-info -->

                <!-- Policies -->

                <div
                    class="site-footer__links-policies"
                    on:click={() => setActiveLinks("policies")}
                >
                    <strong class="site-footer__links-heading">Policies</strong>
                    <ul
                        class="site-footer__links-list"
                        class:hide-below-sm={activeLinks !== "policies"}
                    >
                        <li class="site-footer__link">
                            <a href="http://propublica.org/code-of-ethics/"
                                >Code of Ethics</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/advertising/"
                                >Advertising Policy</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/legal/"
                                >Privacy Policy</a
                            >
                        </li>
                    </ul>
                </div>
                <!-- end .site-footer__links-policies -->

                <!-- Follow -->

                <div
                    class="site-footer__links-follow"
                    on:click={() => setActiveLinks("follow")}
                >
                    <strong class="site-footer__links-heading">Follow</strong>
                    <ul
                        class="site-footer__links-list"
                        class:hide-below-sm={activeLinks !== "follow"}
                    >
                        <li class="site-footer__link">
                            <a
                                href="http://propublica.org/newsletters/"
                                data-pp-view=""
                                data-pp-action="visit">Newsletters</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a
                                href="https://itunes.apple.com/us/app/propublica/id355298887?mt=8"
                                >iOS</a
                            >
                            and
                            <a
                                href="https://play.google.com/store/apps/details?id=com.propublica&amp;hl=en"
                                >Android</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a
                                href="http://feeds.propublica.org/propublica/main"
                                >RSS Feed</a
                            >
                        </li>
                    </ul>
                </div>
                <!-- end .site-footer__links-follow -->

                <!-- More -->

                <div
                    class="site-footer__links-more"
                    on:click={() => setActiveLinks("more")}
                >
                    <strong class="site-footer__links-heading">More</strong>
                    <ul
                        class="site-footer__links-list"
                        class:hide-below-sm={activeLinks !== "more"}
                    >
                        <li class="site-footer__link">
                            <a href="http://propublica.org/tips/"
                                >Send Us Tips</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/steal-our-stories/"
                                >Steal Our Stories</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a
                                href="http://p53lf57qovyuvwsc6xnrppyply3vtqm7l6pcobkmyqsiofyeznfu5uqd.onion/"
                                >Browse via Tor</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a href="http://propublica.org/contact/"
                                >Contact Us</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a
                                href="http://propublica.org/donate/"
                                data-pp-view=""
                                data-pp-action="visit classy">Donate</a
                            >
                        </li>
                        <li class="site-footer__link">
                            <a
                                href="http://propublica.org/support/other-ways-to-give/"
                                >More Ways to Give</a
                            >
                        </li>
                    </ul>
                </div>
                <!-- end .site-footer__links-more -->
            </nav>
            <!-- end .site-footer__section-links -->
        </div>
        <!-- end .site-footer__wrap-inner -->
    </div>
    <!-- end .site-footer__wrap-outer -->
</footer>

{#if !disableNewsletterModal}
    <NewsletterModal />
{/if}

<style lang="scss">
    @import "@propublica/pp-v5-shared/src/css/config/typography.vars";
    @import "@propublica/pp-v5-shared/src/css/config/spacing";
    @import "@propublica/pp-v5-shared/src/css/config/column-settings";
    @import "@propublica/pp-v5-shared/src/css/config/column-setter";
    @import "@propublica/pp-v5-shared/src/css/utils/dark-mode.mixins";

    //----------------------------------------
    // NEWSLETTER
    //----------------------------------------

    .site-footer__section-newsletter {
        :global(.text-dark) &,
        & {
            @include set-light-dark(background, var(--cool-80), var(--cool-70));
        }
        :global(.text-light) & {
            @include set-light-dark(background, var(--cool-70), var(--cool-70));
        }

        padding: var(--spacing2) 0;
    }

    @include grid(site-footer__newsletter-wrap);

    .site-footer__newsletter-wrap {
        max-width: 1344px;
        margin: 0 1.44rem;
        @media screen and (min-width: 48em) {
            margin: 0 2.985984rem;
        }
        @media screen and (min-width: 1439.551488px) {
            margin: 0 auto;
        }

        @include breakpoint-min(md) {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
        }
        @include breakpoint-min(xl) {
            justify-content: center;
        }
    }

    .site-footer__newsletter-heading {
        color: var(--white);
        display: block;
        font-size: var(--scale1);
        line-height: var(--line-height-2);

        @include breakpoint-max(md) {
            margin-bottom: var(--spacing-1);
        }
        @include breakpoint-min(md) {
            width: colspan(4, 8);
        }
        @include breakpoint-min(lg) {
            font-size: var(--scale2);
            width: colspan(6, 12);
        }
        @include breakpoint-min(xl) {
            margin-right: var(--spacing2);
            width: colspan(6, 14);
        }
    }

    .newsletter-signup__input-wrap {
        // prettier-ignore
        :global(.text-dark) &, & {
        @include set-light-dark(background, var(--white), transparent);
        @include set-light-dark(border, none, 1px solid var(--cool-20));
    }
        // prettier-ignore
        :global(.text-light) &, & {
        @include set-light-dark(background, transparent, transparent);
        @include set-light-dark(border, 1px solid var(--cool-20), 1px solid var(--cool-20));
    }

        border-radius: 0.2em;
        font-size: var(--scale0);
        padding: var(--spacing-2) var(--spacing-1) var(--spacing-2)
            var(--spacing0);
        position: relative;
        margin: 0 auto;

        @include breakpoint-min(md) {
            margin: 0;
            width: colspan(4, 8);
        }
        @include breakpoint-min(lg) {
            width: colspan(6, 12);
        }
        @include breakpoint-min(xl) {
            width: colspan(6, 14);
        }
    }

    //----------------------------------------
    // SITE FOOTER
    //----------------------------------------

    .site-footer {
        :global(.text-dark) &,
        & {
            @include set-light-dark(background, var(--cool-10), var(--cool-80));
        }
        :global(.text-light) & {
            @include set-light-dark(background, var(--cool-80), var(--cool-80));
        }

        clear: both;
        font-family: var(--fonts-sans);
        font-size: var(--scale-1);
        margin-top: var(--spacing3);

        a {
            color: inherit;
            text-decoration: none;

            &:focus,
            &:hover {
                :global(.text-dark) &,
                & {
                    color: var(--color-accent-70);
                }
                :global(.text-light) & {
                    @include set-light-dark(
                        color,
                        var(--color-accent-30),
                        var(--color-accent-30)
                    );
                }

                text-decoration: underline;
                text-decoration-color: currentColor;
                text-decoration-thickness: 0.1em;
                text-underline-offset: 0.1em;
            }
        }
    }

    @include grid(site-footer__wrap-outer);

    .site-footer__wrap-outer {
        max-width: 1344px;
        margin: 0 1.44rem;
        @media screen and (min-width: 48em) {
            margin: 0 2.985984rem;
        }
        @media screen and (min-width: 1439.551488px) {
            margin: 0 auto;
        }
    }

    .site-footer__wrap-inner {
        display: flex;
        flex-direction: column-reverse;
        padding: var(--spacing1) 0;

        @include breakpoint-min(md) {
            display: grid;
            grid-gap: colspan(g, 8);
            grid-template-areas: "logo links links links";
            grid-template-columns: 1fr 1fr 1fr 1fr;
        }

        @include breakpoint-min(xl) {
            padding-left: colspan(1 + g, 14);
            padding-right: colspan(1 + g, 14);
        }
    }

    //----------------------------------------
    // LOGO
    //----------------------------------------

    .site-footer__section-logo {
        font-size: var(--scale-2);
        grid-area: logo;
        line-height: var(--line-height-2);

        @include breakpoint-max(md) {
            margin: var(--spacing0) 0;
        }

        > * + * {
            margin-top: var(--spacing-1);
        }
    }

    .site-footer__wordmark {
        :global(.text-dark) &,
        & {
            @include set-light-dark(fill, var(--cool-80), var(--cool-10));
        }
        :global(.text-light) & {
            @include set-light-dark(fill, var(--cool-10), var(--cool-10));
        }

        display: block;
        max-width: 20em;
    }

    .site-footer__tagline {
        display: block;
        font-family: var(--fonts-serif);
        margin-top: var(--spacing-2);
    }

    //----------------------------------------
    // LINKS
    //----------------------------------------

    .site-footer__section-links {
        grid-area: links;

        @include breakpoint-min(sm) {
            column-count: 3;
            column-gap: colspan(g, 6);

            > * {
                break-inside: avoid;
            }
        }

        @include breakpoint-min(md) {
            grid-gap: colspan(g, 8);
        }

        > *:nth-child(1n + 2) {
            @include breakpoint-max(sm) {
                border-top: 1px solid;
                :global(.text-dark) &,
                & {
                    @include set-light-dark(
                        border-color,
                        var(--cool-30),
                        var(--cool-70)
                    );
                }
                :global(.text-light) & {
                    @include set-light-dark(
                        border-color,
                        var(--cool-70),
                        var(--cool-70)
                    );
                }
            }
        }
    }

    .site-footer__links-heading {
        display: block;
        letter-spacing: 0.05em;
        text-transform: uppercase;

        @include breakpoint-max(sm) {
            padding: var(--spacing-1) 0;

            &:hover {
                cursor: pointer;
            }
        }
    }

    .site-footer__links-list {
        margin-bottom: var(--spacing-1);
    }
</style>
